import { useState, useEffect } from 'react';
import { ArrowUpToLine } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useAlert } from '@/hooks/useAlert';
import { formatAmount } from '@/contabilidad/utils/formatters';
import { NumberInput } from '@/components/ui/NumberInput';
import type { Socio, CuentaBancaria, ConfirmarRepartoPayload, DistribucionUtilidad, FondoContable } from '@/contabilidad/types';
import { cn } from '@/common';
import { FONDO_LABELS, NATIVE_FIELD_CLASS } from '@/contabilidad/constants';

interface RepartirUtilidadModalProps {
  open: boolean;
  onClose: () => void;
  /** Fondo del que sale el reparto; `utilidadNeta` es el disponible de ese fondo. */
  fondo: FondoContable;
  utilidadNeta: { ARS: number; USD: number };
  socios: Socio[];
  cajas: CuentaBancaria[];
  onConfirmar: (payload: ConfirmarRepartoPayload) => Promise<DistribucionUtilidad>;
  isConfirmando?: boolean;
}

function getInitials(nombre: string): string {
  return nombre
    .split(' ')
    .map(n => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

/** Formatea un monto mostrando siempre "$" para ARS y "US$" para USD */
function displayMonto(value: number, moneda: 'ARS' | 'USD'): string {
  const sign = value < 0 ? '-' : '';
  const num = formatAmount(value, moneda); // formatAmount ya aplica abs internamente
  return moneda === 'ARS' ? `${sign}$ ${num}` : `${sign}US$ ${num}`;
}

type AsignacionSocio = {
  ARS: string;
  USD: string;
  porcentajeARS: string;
  porcentajeUSD: string;
};

function toCents(value: string | number): number {
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Math.round((Number.isFinite(parsed) ? parsed : 0) * 100);
}

function distribuirCentavos(total: number, socios: Socio[]): Record<number, number> {
  if (socios.length === 0) return {};

  const sumPct = socios.reduce((sum, socio) => sum + socio.porcentaje, 0);
  const bases = socios.map((socio) => {
    const weight = sumPct > 0 ? socio.porcentaje / sumPct : 1 / socios.length;
    const exacto = total * weight;
    return { id: socio.id, centavos: Math.floor(exacto), resto: exacto - Math.floor(exacto) };
  });
  const faltantes = total - bases.reduce((sum, item) => sum + item.centavos, 0);
  const porResto = [...bases].sort((a, b) => b.resto - a.resto || a.id - b.id);

  for (let i = 0; i < faltantes; i += 1) {
    porResto[i % porResto.length].centavos += 1;
  }

  return Object.fromEntries(bases.map(item => [item.id, item.centavos]));
}

function crearAsignaciones(
  socios: Socio[],
  seleccionados: Set<number>,
  montoARS: number,
  montoUSD: number,
): Record<number, AsignacionSocio> {
  const participantes = socios.filter(socio => seleccionados.has(socio.id));
  const ars = distribuirCentavos(toCents(montoARS), participantes);
  const usd = distribuirCentavos(toCents(montoUSD), participantes);

  return Object.fromEntries(socios.map(socio => [
    socio.id,
    {
      ARS: String((ars[socio.id] ?? 0) / 100),
      USD: String((usd[socio.id] ?? 0) / 100),
      porcentajeARS: String(montoARS > 0 ? Math.round(((ars[socio.id] ?? 0) / toCents(montoARS)) * 10000) / 100 : 0),
      porcentajeUSD: String(montoUSD > 0 ? Math.round(((usd[socio.id] ?? 0) / toCents(montoUSD)) * 10000) / 100 : 0),
    },
  ]));
}

function sincronizarPorcentajes(
  asignaciones: Record<number, AsignacionSocio>,
  montoARS: number,
  montoUSD: number,
): Record<number, AsignacionSocio> {
  return Object.fromEntries(Object.entries(asignaciones).map(([id, asignacion]) => [
    id,
    {
      ...asignacion,
      porcentajeARS: String(montoARS > 0
        ? Math.round((toCents(asignacion.ARS) / toCents(montoARS)) * 10000) / 100
        : 0),
      porcentajeUSD: String(montoUSD > 0
        ? Math.round((toCents(asignacion.USD) / toCents(montoUSD)) * 10000) / 100
        : 0),
    },
  ]));
}

export function RepartirUtilidadModal({
  open,
  onClose,
  fondo,
  utilidadNeta,
  socios,
  cajas,
  onConfirmar,
  isConfirmando = false,
}: RepartirUtilidadModalProps) {
  const { successAlert, errorAlert } = useAlert();

  const [montoARSInput, setMontoARSInput] = useState('');
  const [montoUSDInput, setMontoUSDInput] = useState('');
  const [origenes, setOrigenes] = useState<Record<number, string>>({});
  const [sociosSeleccionados, setSociosSeleccionados] = useState<Set<number>>(new Set());
  const [asignacionesSocios, setAsignacionesSocios] = useState<Record<number, AsignacionSocio>>({});
  const [asignacionManual, setAsignacionManual] = useState(false);

  useEffect(() => {
    if (open) {
      setMontoARSInput(utilidadNeta.ARS > 0 ? String(utilidadNeta.ARS) : '');
      setMontoUSDInput(utilidadNeta.USD > 0 ? String(utilidadNeta.USD) : '');
      setOrigenes({});
      // Por defecto reparte entre todos los socios (comportamiento proporcional actual)
      const seleccionados = new Set(socios.map(s => s.id));
      setSociosSeleccionados(seleccionados);
      setAsignacionesSocios(crearAsignaciones(socios, seleccionados, utilidadNeta.ARS, utilidadNeta.USD));
      setAsignacionManual(false);
    }
  }, [open, utilidadNeta.ARS, utilidadNeta.USD, socios]);

  const toggleSocio = (id: number) => {
    setSociosSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      setAsignacionesSocios(crearAsignaciones(socios, next, montoARS, montoUSD));
      setAsignacionManual(false);
      return next;
    });
  };

  const arsEnPerdida = utilidadNeta.ARS <= 0;
  const usdEnPerdida = utilidadNeta.USD <= 0;
  const ambasEnPerdida = arsEnPerdida && usdEnPerdida;

  const montoARS = arsEnPerdida ? 0 : Math.round((parseFloat(montoARSInput) || 0) * 100) / 100;
  const montoUSD = usdEnPerdida ? 0 : Math.round((parseFloat(montoUSDInput) || 0) * 100) / 100;

  const handleMontoChange = (moneda: 'ARS' | 'USD', value: string) => {
    const nextARS = moneda === 'ARS' ? Math.max(0, toCents(value)) / 100 : montoARS;
    const nextUSD = moneda === 'USD' ? Math.max(0, toCents(value)) / 100 : montoUSD;
    if (moneda === 'ARS') setMontoARSInput(value);
    else setMontoUSDInput(value);

    if (!asignacionManual) {
      setAsignacionesSocios(crearAsignaciones(socios, sociosSeleccionados, nextARS, nextUSD));
    } else {
      setAsignacionesSocios(prev => sincronizarPorcentajes(prev, nextARS, nextUSD));
    }
  };

  // Porcentaje derivado de los montos, para el slider (atajo) y para informar al backend
  const pctARS = utilidadNeta.ARS > 0 ? (montoARS / utilidadNeta.ARS) * 100 : 0;
  const pctUSD = utilidadNeta.USD > 0 ? (montoUSD / utilidadNeta.USD) * 100 : 0;
  const sliderPct = Math.min(100, Math.round(Math.max(pctARS, pctUSD)));
  const porcentajePayload = Math.min(100, Math.max(0.01, Math.round(Math.max(pctARS, pctUSD) * 100) / 100));

  /** El slider es un atajo: rellena ambos montos al % elegido de lo disponible */
  const handleSliderChange = (pct: number) => {
    const nextARS = arsEnPerdida ? 0 : Math.round((utilidadNeta.ARS * pct) / 100 * 100) / 100;
    const nextUSD = usdEnPerdida ? 0 : Math.round((utilidadNeta.USD * pct) / 100 * 100) / 100;
    if (!arsEnPerdida) {
      setMontoARSInput(String(nextARS));
    }
    if (!usdEnPerdida) {
      setMontoUSDInput(String(nextUSD));
    }
    if (!asignacionManual) {
      setAsignacionesSocios(crearAsignaciones(socios, sociosSeleccionados, nextARS, nextUSD));
    } else {
      setAsignacionesSocios(prev => sincronizarPorcentajes(prev, nextARS, nextUSD));
    }
  };

  const sociosSeleccionadosList = socios.filter(s => sociosSeleccionados.has(s.id));
  const montoSocio = (id: number, moneda: 'ARS' | 'USD') =>
    Math.max(0, toCents(asignacionesSocios[id]?.[moneda] ?? '0')) / 100;
  const totalSociosARS = sociosSeleccionadosList.reduce((sum, socio) => sum + toCents(montoSocio(socio.id, 'ARS')), 0);
  const totalSociosUSD = sociosSeleccionadosList.reduce((sum, socio) => sum + toCents(montoSocio(socio.id, 'USD')), 0);
  const asignacionARSCompleta = totalSociosARS === toCents(montoARS);
  const asignacionUSDCompleta = totalSociosUSD === toCents(montoUSD);

  const handleAsignacionChange = (id: number, moneda: 'ARS' | 'USD', value: string) => {
    setAsignacionManual(true);
    setAsignacionesSocios(prev => ({
      ...prev,
      [id]: {
        ARS: prev[id]?.ARS ?? '0',
        USD: prev[id]?.USD ?? '0',
        porcentajeARS: prev[id]?.porcentajeARS ?? '0',
        porcentajeUSD: prev[id]?.porcentajeUSD ?? '0',
        [moneda]: value,
        [moneda === 'ARS' ? 'porcentajeARS' : 'porcentajeUSD']: String(
          moneda === 'ARS'
            ? (montoARS > 0 ? Math.round((toCents(value) / toCents(montoARS)) * 10000) / 100 : 0)
            : (montoUSD > 0 ? Math.round((toCents(value) / toCents(montoUSD)) * 10000) / 100 : 0),
        ),
      },
    }));
  };

  const handlePorcentajeChange = (id: number, moneda: 'ARS' | 'USD', value: string) => {
    const porcentaje = Math.min(100, Math.max(0, Number.parseFloat(value) || 0));
    const total = moneda === 'ARS' ? montoARS : montoUSD;
    const montoCalculado = Math.round(total * porcentaje) / 100;
    const porcentajeKey = moneda === 'ARS' ? 'porcentajeARS' : 'porcentajeUSD';

    setAsignacionManual(true);
    setAsignacionesSocios(prev => ({
      ...prev,
      [id]: {
        ARS: prev[id]?.ARS ?? '0',
        USD: prev[id]?.USD ?? '0',
        porcentajeARS: prev[id]?.porcentajeARS ?? '0',
        porcentajeUSD: prev[id]?.porcentajeUSD ?? '0',
        [moneda]: String(montoCalculado),
        [porcentajeKey]: value,
      },
    }));
  };

  const cajasARS = cajas.filter(c => c.moneda === 'ARS');
  const cajasUSD = cajas.filter(c => c.moneda === 'USD');
  const activeCajasARS = arsEnPerdida ? [] : cajasARS;
  const activeCajasUSD = usdEnPerdida ? [] : cajasUSD;

  const parseOrigen = (id: number): number => {
    const val = parseFloat(origenes[id] ?? '0');
    return isNaN(val) ? 0 : val;
  };

  const totalOrigenARS = cajasARS.reduce((sum, c) => sum + parseOrigen(c.id), 0);
  const totalOrigenUSD = cajasUSD.reduce((sum, c) => sum + parseOrigen(c.id), 0);

  const faltanARS = montoARS - totalOrigenARS;
  const faltanUSD = montoUSD - totalOrigenUSD;

  const saldoDisponible = (caja: CuentaBancaria) => Math.max(0, caja.saldo);

  const isExceeded = (caja: CuentaBancaria) => {
    const val = parseOrigen(caja.id);
    return val > 0 && val > saldoDisponible(caja) + 0.01;
  };

  /** Completa el origen de una caja con el máximo posible: lo que falte cubrir del monto,
   *  topeado por el saldo disponible de esa caja. */
  const handleMaxOrigen = (caja: CuentaBancaria, montoObjetivo: number, totalOrigenMoneda: number) => {
    const otros = totalOrigenMoneda - parseOrigen(caja.id);
    const falta = Math.max(0, montoObjetivo - otros);
    const max = Math.min(saldoDisponible(caja), falta);
    setOrigenes(prev => ({ ...prev, [caja.id]: String(Math.round(max * 100) / 100) }));
  };

  const hayMonto = montoARS > 0 || montoUSD > 0;

  const canConfirm =
    hayMonto &&
    !ambasEnPerdida &&
    sociosSeleccionados.size > 0 &&
    asignacionARSCompleta &&
    asignacionUSDCompleta &&
    (arsEnPerdida || Math.abs(faltanARS) < 0.5) &&
    (usdEnPerdida || Math.abs(faltanUSD) < 0.5) &&
    [...activeCajasARS, ...activeCajasUSD].every(c => !isExceeded(c));

  async function handleConfirmar() {
    const origenesPayload = [...activeCajasARS, ...activeCajasUSD]
      .map(c => ({ cajaId: c.id, monto: parseOrigen(c.id) }))
      .filter(o => o.monto > 0);

    try {
      await onConfirmar({
        fondo,
        porcentaje: porcentajePayload,
        montoARS,
        montoUSD,
        origenes: origenesPayload,
        socios: sociosSeleccionadosList
          .map(socio => ({
            socioId: socio.id,
            montoARS: montoSocio(socio.id, 'ARS'),
            montoUSD: montoSocio(socio.id, 'USD'),
          }))
          .filter(socio => socio.montoARS > 0 || socio.montoUSD > 0),
      });
      successAlert({
        title: 'Reparto confirmado',
        message: 'La distribución de utilidades fue registrada correctamente.',
      });
      onClose();
    } catch (err) {
      errorAlert({ title: 'Error al confirmar reparto', exception: err });
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            Simulador de Reparto — {FONDO_LABELS[fondo]}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-1">
          {/* Slider porcentaje */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">Porcentaje a repartir</span>
              <span className="text-sm font-bold text-primary">{sliderPct}%</span>
            </div>
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={sliderPct}
              onChange={e => handleSliderChange(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary"
            />
          </div>

          {/* Cards de montos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className={`rounded-lg border p-4 text-center ${arsEnPerdida ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Pesos</p>
              {arsEnPerdida ? (
                <>
                  <p className="text-xl font-bold tabular-nums text-red-600">
                    ARS {displayMonto(montoARS, 'ARS')}
                  </p>
                  <p className="text-xs text-red-500 mt-1 font-medium">Pérdida neta</p>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-lg font-bold text-gray-900">$</span>
                    <NumberInput
                      id="monto-ars-total"
                      decimals={2}
                      min={0}
                      max={utilidadNeta.ARS}
                      value={montoARSInput}
                      onChange={value => handleMontoChange('ARS', value)}
                      onAccept={value => handleMontoChange('ARS', value === null ? '' : String(value))}
                      placeholder="0"
                      className={cn(NATIVE_FIELD_CLASS, 'px-1 py-1 h-auto text-xl font-bold tabular-nums bg-transparent text-center w-28')}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Disponible: {displayMonto(utilidadNeta.ARS, 'ARS')}
                  </p>
                </>
              )}
            </div>
            <div className={`rounded-lg border p-4 text-center ${usdEnPerdida ? 'bg-red-50 border-red-200' : 'bg-slate-50'}`}>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Dólares</p>
              {usdEnPerdida ? (
                <>
                  <p className="text-xl font-bold tabular-nums text-red-600">
                    USD {displayMonto(montoUSD, 'USD')}
                  </p>
                  <p className="text-xs text-red-500 mt-1 font-medium">Pérdida neta</p>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-center gap-1">
                    <span className="text-lg font-bold text-gray-900">US$</span>
                    <NumberInput
                      id="monto-usd-total"
                      decimals={2}
                      min={0}
                      max={utilidadNeta.USD}
                      value={montoUSDInput}
                      onChange={value => handleMontoChange('USD', value)}
                      onAccept={value => handleMontoChange('USD', value === null ? '' : String(value))}
                      placeholder="0"
                      className={cn(NATIVE_FIELD_CLASS, 'px-1 py-1 h-auto text-xl font-bold tabular-nums bg-transparent text-center w-28')}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Disponible: {displayMonto(utilidadNeta.USD, 'USD')}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Origen de Fondos */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Origen de Fondos
            </p>

            {/* Sub-sección ARS */}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-semibold text-muted-foreground">ARS</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              {arsEnPerdida ? (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-center">
                  <p className="text-sm text-red-600 font-medium">Pérdida neta en ARS — no se requieren fondos</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cajasARS.map(caja => {
                    const exceeded = isExceeded(caja);
                    return (
                      <div key={caja.id} className="flex flex-col gap-0.5">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:flex-nowrap">
                          <span className="text-sm font-medium w-full sm:w-36 shrink-0 truncate">{caja.nombre}</span>
                          <span className="text-xs text-muted-foreground flex-1 whitespace-nowrap">
                            Disponible: {displayMonto(caja.saldo, 'ARS')}
                          </span>
                          <div className="flex items-center gap-1.5 w-40 shrink-0">
                            <NumberInput
                              id="monto"
                              decimals={2}
                              min={0}
                              value={origenes[caja.id] ?? ''}
                              onChange={(value) => setOrigenes(prev => ({ ...prev, [caja.id]: value }))}
                              placeholder='0'
                              className={cn(NATIVE_FIELD_CLASS, exceeded && 'border-rose-500 focus:border-rose-500', 'flex-1 px-2 py-1.5 text-base md:text-sm bg-transparent outline-none tabular-nums w-full text-right')}
                            />
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="xs"
                                  aria-label="Completar con el máximo disponible"
                                  onClick={() => handleMaxOrigen(caja, montoARS, totalOrigenARS)}
                                >
                                  <ArrowUpToLine className="size-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Completar con el máximo disponible</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                        {exceeded && (
                          <p className="text-xs text-red-500 text-right">Supera el saldo disponible</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Sub-sección USD */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-semibold text-muted-foreground">USD</span>
                <div className="h-px flex-1 bg-border" />
              </div>
              {usdEnPerdida ? (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-center">
                  <p className="text-sm text-red-600 font-medium">Pérdida neta en USD — no se requieren fondos</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cajasUSD.map(caja => {
                    const exceeded = isExceeded(caja);
                    return (
                      <div key={caja.id} className="flex flex-col gap-0.5">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:flex-nowrap">
                          <span className="text-sm font-medium w-full sm:w-36 shrink-0 truncate">{caja.nombre}</span>
                          <span className="text-xs text-muted-foreground flex-1 whitespace-nowrap">
                            Disponible: {displayMonto(caja.saldo, 'USD')}
                          </span>
                          <div className="flex items-center gap-1.5 w-40 shrink-0">
                            <NumberInput
                              id="monto-usd"
                              decimals={2}
                              min={0}
                              value={origenes[caja.id] ?? ''}
                              onChange={(value) => setOrigenes(prev => ({ ...prev, [caja.id]: value }))}
                              placeholder='0'
                              className={cn(NATIVE_FIELD_CLASS, exceeded && 'border-rose-500 focus:border-rose-500', 'flex-1 px-2 py-1.5 text-base md:text-sm bg-transparent outline-none tabular-nums w-full text-right')}
                            />
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="xs"
                                  aria-label="Completar con el máximo disponible"
                                  onClick={() => handleMaxOrigen(caja, montoUSD, totalOrigenUSD)}
                                >
                                  <ArrowUpToLine className="size-3.5" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Completar con el máximo disponible</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                        {exceeded && (
                          <p className="text-xs text-red-500 text-right">Supera el saldo disponible</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Indicadores de estado */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <div className={`rounded-md p-3 text-center text-sm font-semibold ${arsEnPerdida
                ? 'bg-red-50 text-red-600'
                : Math.abs(faltanARS) < 0.5
                  ? 'bg-green-50 text-green-700'
                  : 'bg-amber-50 text-amber-700'
                }`}>
                <p className="text-xs font-normal opacity-75 mb-0.5">Pesos (ARS)</p>
                {arsEnPerdida
                  ? 'Pérdida neta'
                  : Math.abs(faltanARS) < 0.5
                    ? '✓ Completo'
                    : `Faltan ${displayMonto(faltanARS, 'ARS')}`}
              </div>
              <div className={`rounded-md p-3 text-center text-sm font-semibold ${usdEnPerdida
                ? 'bg-red-50 text-red-600'
                : Math.abs(faltanUSD) < 0.5
                  ? 'bg-green-50 text-green-700'
                  : 'bg-amber-50 text-amber-700'
                }`}>
                <p className="text-xs font-normal opacity-75 mb-0.5">Dólares (USD)</p>
                {usdEnPerdida
                  ? 'Pérdida neta'
                  : Math.abs(faltanUSD) < 0.5
                    ? '✓ Completo'
                    : `Faltan ${displayMonto(faltanUSD, 'USD')}`}
              </div>
            </div>
          </div>

          {/* Distribución por Socio */}
          {socios.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Distribución por Socio
              </p>
              <p className="text-xs text-muted-foreground mb-2">
                Definí cuánto retira cada socio en cada moneda. Las sumas deben coincidir con
                los montos totales del reparto.
              </p>
              <div className="space-y-2">
                {socios.map(socio => {
                  const seleccionado = sociosSeleccionados.has(socio.id);
                  return (
                    <div
                      key={socio.id}
                      className={cn(
                        'grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_13rem_13rem] items-center gap-3 py-2 border-b last:border-0',
                        !seleccionado && 'opacity-50',
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={seleccionado}
                          onChange={() => toggleSocio(socio.id)}
                          className="size-4 accent-primary shrink-0"
                          aria-label={`Incluir a ${socio.nombre} en el reparto`}
                        />
                        <div className="size-9 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
                          {getInitials(socio.nombre)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{socio.nombre}</p>
                          <p className="text-xs text-muted-foreground">
                            Configurado: {socio.porcentaje}%
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold text-muted-foreground mb-1">ARS</p>
                        <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
                          <div>
                            <label htmlFor={`socio-${socio.id}-porcentaje-ars`} className="block text-[10px] text-muted-foreground mb-0.5">
                              Porcentaje
                            </label>
                            <div className="relative">
                              <NumberInput
                                id={`socio-${socio.id}-porcentaje-ars`}
                                decimals={2}
                                min={0}
                                max={100}
                                value={asignacionesSocios[socio.id]?.porcentajeARS ?? '0'}
                                onChange={value => handlePorcentajeChange(socio.id, 'ARS', value)}
                                onAccept={value => handlePorcentajeChange(socio.id, 'ARS', value === null ? '0' : String(value))}
                                data-testid={`socio-${socio.id}-porcentaje-ars`}
                                disabled={!seleccionado || arsEnPerdida}
                                className={cn(NATIVE_FIELD_CLASS, 'h-8 pr-5 text-base md:text-sm text-right tabular-nums')}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                            </div>
                          </div>
                          <div>
                            <label htmlFor={`socio-${socio.id}-ars`} className="block text-[10px] text-muted-foreground mb-0.5">
                              Importe
                            </label>
                            <NumberInput
                              id={`socio-${socio.id}-ars`}
                              decimals={2}
                              min={0}
                              max={montoARS}
                              value={asignacionesSocios[socio.id]?.ARS ?? '0'}
                              onChange={value => handleAsignacionChange(socio.id, 'ARS', value)}
                              onAccept={value => handleAsignacionChange(socio.id, 'ARS', value === null ? '0' : String(value))}
                              data-testid={`socio-${socio.id}-ars`}
                              disabled={!seleccionado || arsEnPerdida}
                              className={cn(NATIVE_FIELD_CLASS, 'h-8 text-base md:text-sm text-right tabular-nums')}
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-[11px] font-semibold text-muted-foreground mb-1">USD</p>
                        <div className="grid grid-cols-[5rem_minmax(0,1fr)] gap-2">
                          <div>
                            <label htmlFor={`socio-${socio.id}-porcentaje-usd`} className="block text-[10px] text-muted-foreground mb-0.5">
                              Porcentaje
                            </label>
                            <div className="relative">
                              <NumberInput
                                id={`socio-${socio.id}-porcentaje-usd`}
                                decimals={2}
                                min={0}
                                max={100}
                                value={asignacionesSocios[socio.id]?.porcentajeUSD ?? '0'}
                                onChange={value => handlePorcentajeChange(socio.id, 'USD', value)}
                                onAccept={value => handlePorcentajeChange(socio.id, 'USD', value === null ? '0' : String(value))}
                                data-testid={`socio-${socio.id}-porcentaje-usd`}
                                disabled={!seleccionado || usdEnPerdida}
                                className={cn(NATIVE_FIELD_CLASS, 'h-8 pr-5 text-base md:text-sm text-right tabular-nums')}
                              />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                            </div>
                          </div>
                          <div>
                            <label htmlFor={`socio-${socio.id}-usd`} className="block text-[10px] text-muted-foreground mb-0.5">
                              Importe
                            </label>
                            <NumberInput
                              id={`socio-${socio.id}-usd`}
                              decimals={2}
                              min={0}
                              max={montoUSD}
                              value={asignacionesSocios[socio.id]?.USD ?? '0'}
                              onChange={value => handleAsignacionChange(socio.id, 'USD', value)}
                              onAccept={value => handleAsignacionChange(socio.id, 'USD', value === null ? '0' : String(value))}
                              data-testid={`socio-${socio.id}-usd`}
                              disabled={!seleccionado || usdEnPerdida}
                              className={cn(NATIVE_FIELD_CLASS, 'h-8 text-base md:text-sm text-right tabular-nums')}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className={cn(
                  'rounded-md px-3 py-2 text-xs font-semibold text-center',
                  asignacionARSCompleta ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700',
                )}>
                  ARS asignados: {displayMonto(totalSociosARS / 100, 'ARS')}
                  {!asignacionARSCompleta && ` de ${displayMonto(montoARS, 'ARS')}`}
                </div>
                <div className={cn(
                  'rounded-md px-3 py-2 text-xs font-semibold text-center',
                  asignacionUSDCompleta ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700',
                )}>
                  USD asignados: {displayMonto(totalSociosUSD / 100, 'USD')}
                  {!asignacionUSDCompleta && ` de ${displayMonto(montoUSD, 'USD')}`}
                </div>
              </div>
            </div>
          )}

          {/* Alerta cuando ambas monedas están en pérdida */}
          {ambasEnPerdida && (
            <div className="rounded-md bg-red-50 border border-red-200 p-4 text-center">
              <p className="text-sm font-semibold text-red-700">No hay utilidad disponible para repartir</p>
              <p className="text-xs text-red-500 mt-1">Ambas monedas presentan pérdida neta acumulada.</p>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={onClose} disabled={isConfirmando}>
              Cancelar
            </Button>
            <Button
              className="bg-primary hover:bg-primary/90"
              disabled={!canConfirm || isConfirmando}
              onClick={handleConfirmar}
            >
              {isConfirmando ? 'Confirmando...' : 'Confirmar Reparto'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
